import type { CircuitIR } from "@models/circuit-ir";
import { Alert, Box, CircularProgress } from "@mui/material";
/**
 * Render-source dispatcher per IR plan §7.3.
 *
 * Two modes:
 *
 *   * **Server-rendered SVG** (today's `_render.py` / `freeform_renderer.py`
 *     templates) — when the agent / backend chose `layout_strategy="explicit"`
 *     or matched a known template, the frontend just embeds the served SVG.
 *     Same path as the existing `SchematicSvg` chat bubble.
 *
 *   * **Client-rendered (this PR)** — when no template matched and the
 *     backend wants the general-fallback ELK layout, ship the IR JSON
 *     to the frontend; this resolver fetches it and runs `CircuitCanvas`
 *     which spins the Web Worker.
 *
 * The dispatch lives here so consumers (chat bubbles, project pages,
 * draft panels) don't have to know the difference.
 *
 * Today there's no `RenderResult` token endpoint backend-side — the v1
 * resolver routes purely on what the caller already knows: a `versionId`
 * fetches via `/circuits/versions/{vid}/ir`; an inline `ir` skips the
 * fetch. When the backend ships the token-based render API per §7.3 we
 * extend this with a `renderId` branch.
 */
// Direct import — going through the @features/circuits barrel here
// would re-enter the same module that re-exports this component,
// producing a circular chunk dependency the bundler can't split
// cleanly.
import { useVersionIr } from "../hooks/useCircuitIr";

import { CircuitCanvas } from "./CircuitCanvas";

interface CircuitRenderResolverProps {
  /** Inline IR (e.g. from a snapshot already in memory). Takes
   *  precedence over `versionId`. */
  ir?: CircuitIR | null;
  /** Committed-version id. Hydrates IR via the `/circuits/versions/{id}/ir`
   *  hook. */
  versionId?: string | null;
  /** Server-rendered SVG. When present, the resolver embeds it as-is and
   *  skips ELK entirely. */
  serverSvg?: string | null;
  height?: number;
  /** Forwarded to CircuitCanvas — render a halo on these component ids.
   *  Ignored when `serverSvg` is rendered (no per-component handles). */
  highlightIds?: ReadonlySet<string>;
  /** Same as highlightIds but keyed by refdes. */
  highlightRefdes?: ReadonlySet<string>;
}

export function CircuitRenderResolver({
  ir,
  versionId,
  serverSvg,
  height,
  highlightIds,
  highlightRefdes,
}: CircuitRenderResolverProps) {
  // Server SVG wins — explicit / template paths produce nicer output
  // than ELK can without per-kind symbol rendering.
  if (serverSvg) {
    return (
      <Box
        dangerouslySetInnerHTML={{ __html: serverSvg }}
        sx={{
          "& svg": { display: "block", width: "100%", height: "auto" },
        }}
      />
    );
  }

  if (ir) {
    return (
      <CircuitCanvas
        ir={ir}
        height={height}
        highlightIds={highlightIds}
        highlightRefdes={highlightRefdes}
      />
    );
  }

  return (
    <VersionResolved
      versionId={versionId}
      height={height}
      highlightIds={highlightIds}
      highlightRefdes={highlightRefdes}
    />
  );
}

function VersionResolved({
  versionId,
  height,
  highlightIds,
  highlightRefdes,
}: {
  versionId: string | null | undefined;
  height?: number;
  highlightIds?: ReadonlySet<string>;
  highlightRefdes?: ReadonlySet<string>;
}) {
  const { data, isLoading, error } = useVersionIr(versionId);

  if (!versionId) {
    return null;
  }
  if (isLoading) {
    return (
      <Box
        sx={{
          height: height ?? 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (error || !data) {
    return (
      <Alert severity="error">
        Could not load circuit IR for this version.
      </Alert>
    );
  }
  return (
    <CircuitCanvas
      ir={data.ir}
      height={height}
      highlightIds={highlightIds}
      highlightRefdes={highlightRefdes}
    />
  );
}
